import React from 'react';
import Navbar from '../../components/website/Navbar';
import Footer from '../../components/website/Footer';

const Section = ({ n, title, children }) => (
  <section className="mt-8">
    <h2 className="font-heading font-bold text-xl sm:text-2xl text-rozops-navy tracking-tight">
      {n}. {title}
    </h2>
    <div className="mt-3 text-rozops-text-muted text-[15px] leading-relaxed space-y-2">{children}</div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="mt-2 space-y-1.5">
    {items.map((it) => (
      <li key={it} className="flex items-start gap-2">
        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-rozops-blue flex-shrink-0" />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

export default function Terms() {
  return (
    <div className="bg-rozops-bg min-h-screen">
      <Navbar />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20" data-testid="terms-page">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-rozops-blue-light text-rozops-blue text-xs font-semibold px-3 py-1">Legal</div>
        <h1 className="mt-4 font-heading font-extrabold text-4xl sm:text-5xl text-rozops-navy tracking-tight">Terms of Service</h1>
        <p className="mt-2 text-sm text-rozops-text-muted">Effective Date: 14th July, 2026.</p>

        <div className="mt-8 bg-white rounded-2xl border border-gray-100 shadow-soft p-6 sm:p-8">
          <p className="text-rozops-text-muted text-[15px] leading-relaxed">Welcome to RozOps.</p>
          <p className="mt-3 text-rozops-text-muted text-[15px] leading-relaxed">
            These Terms of Service govern your use of the RozOps website, dashboards, applications, APIs, and related services.
          </p>
          <p className="mt-3 text-rozops-text-muted text-[15px] leading-relaxed">
            By accessing or using RozOps, you agree to these Terms.
          </p>

          <Section n={1} title="About RozOps">
            <p>
              RozOps is a software platform operated by <span className="font-semibold text-rozops-text">Nexwis Innovations Private Limited</span> that helps businesses manage customers, subscriptions, deliveries, billing, payments, and business operations.
            </p>
          </Section>

          <Section n={2} title="Eligibility">
            <p>You must be legally capable of entering into a binding agreement and comply with applicable laws while using RozOps.</p>
          </Section>

          <Section n={3} title="Account Responsibilities">
            <p>You are responsible for:</p>
            <Bullets items={[
              'Maintaining account security',
              'Protecting login credentials',
              'Ensuring information is accurate',
              'Activities performed under your account',
            ]} />
          </Section>

          <Section n={4} title="Merchant Responsibilities">
            <p>Merchants are responsible for:</p>
            <Bullets items={[
              'Maintaining accurate customer information',
              'Obtaining necessary customer permissions',
              'Complying with applicable tax and business laws',
              'Managing product pricing and invoices',
              'Ensuring lawful use of customer data',
            ]} />
          </Section>

          <Section n={5} title="Customer Responsibilities">
            <p>Customers are responsible for:</p>
            <Bullets items={[
              'Providing accurate account information',
              'Reviewing invoices',
              'Reporting discrepancies promptly',
              'Protecting account credentials',
            ]} />
          </Section>

          <Section n={6} title="Acceptable Use">
            <p>You agree not to:</p>
            <Bullets items={[
              'Use RozOps for unlawful purposes',
              'Upload malicious software',
              'Attempt unauthorized access',
              'Interfere with platform operations',
              'Misrepresent your identity',
              'Upload illegal or harmful content',
            ]} />
          </Section>

          <Section n={7} title="Intellectual Property">
            <p>RozOps, including its software, branding, design, content, and technology, is the intellectual property of Nexwis Innovations Private Limited unless otherwise stated.</p>
            <p>You may not copy, modify, distribute, or reverse engineer the platform without written permission.</p>
          </Section>

          <Section n={8} title="Service Availability">
            <p>We strive to provide reliable services but do not guarantee uninterrupted or error-free availability.</p>
            <p>Maintenance, upgrades, or unforeseen technical issues may temporarily affect service availability.</p>
          </Section>

          <Section n={9} title="Third-Party Services">
            <p>RozOps may integrate with third-party payment gateways, communication services, cloud providers, or APIs. We are not responsible for services provided by third parties.</p>
          </Section>

          <Section n={10} title="Limitation of Liability">
            <p>To the maximum extent permitted by law, Nexwis Innovations Private Limited shall not be liable for indirect, incidental, consequential, or special damages arising from the use of RozOps.</p>
          </Section>

          <Section n={11} title="Termination">
            <p>We may suspend or terminate accounts that violate these Terms or applicable laws.</p>
            <p>Users may discontinue using RozOps at any time.</p>
          </Section>

          <Section n={12} title="Governing Law">
            <p>These Terms shall be governed by the laws of India.</p>
            <p>Any disputes shall be subject to the jurisdiction of the competent courts located in Hyderabad, Telangana.</p>
          </Section>

          <Section n={13} title="Changes to Terms">
            <p>We may modify these Terms periodically. Continued use of RozOps after changes constitutes acceptance of the updated Terms.</p>
          </Section>

          <Section n={14} title="Contact Information">
            <div className="mt-2 grid gap-1.5 text-[15px]">
              <div><span className="font-semibold text-rozops-text">Brand:</span> RozOps</div>
              <div><span className="font-semibold text-rozops-text">Company:</span> Nexwis Innovations Private Limited</div>
              <div><span className="font-semibold text-rozops-text">Contact Person:</span> Kranthi Kumar K</div>
              <div><span className="font-semibold text-rozops-text">Mobile:</span> <a href="tel:+917672002525" className="text-rozops-blue">+91 7672002525</a></div>
              <div><span className="font-semibold text-rozops-text">Email:</span> <a href="mailto:hello@rozops.com" className="text-rozops-blue">hello@rozops.com</a></div>
              <div className="mt-3">
                <span className="font-semibold text-rozops-text">Registered Address:</span>
                <div className="text-rozops-text-muted mt-1 leading-relaxed">
                  Nexwis Innovations Private Limited<br />
                  T-HUB FOUNDATION, SY NO. 83/1,<br />
                  Raidurg, Hyderabad, Telangana, India – 500081
                </div>
              </div>
            </div>
          </Section>
        </div>
      </article>
      <Footer />
    </div>
  );
}
